/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import LanguageLink from "../routing/LanguageLink";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import api from "../../utils/api";
import ScrollAnimation from "../../hooks/ScrollAnimation";
import PlanDetailsModal from "../ui/PlanDetailsModal";

const PlansSection = () => {
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const {
    data: plansData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => {
      const res = await api.get("/plans");
      return res.data.data
        .filter((p) => p.is_active)
        .sort((a, b) => a.sort_order - b.sort_order);
    },
  });

  const { data: planDetails, isFetching: isFetchingDetails } = useQuery({
    queryKey: ["plan", selectedPlanId],
    queryFn: async () => {
      const res = await api.get(`/plans/${selectedPlanId}`);
      return res.data.data;
    },
    enabled: !!selectedPlanId,
  });

  if (isLoading) {
    return (
      <section className="py-16 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-64 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 text-center text-red-600">
        Failed to load plans. Please try again later.
      </section>
    );
  }

  return (
    <>
      <section className="relative bg-gradient-to-b from-white to-blue-50 py-8 lg:py-10 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute -top-40 -right-40 w-96 h-96 bg-blue-200 rounded-full opacity-20 blur-3xl"
            animate={{ scale: [1, 1.2, 1], x: [0, 50, 0], y: [0, 30, 0] }}
            transition={{ duration: 20, repeat: Infinity }}
          />
          <motion.div
            className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-200 rounded-full opacity-20 blur-3xl"
            animate={{ scale: [1, 1.3, 1], x: [0, -50, 0], y: [0, -30, 0] }}
            transition={{ duration: 18, repeat: Infinity }}
          />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs font-semibold mb-2">
                Pricing & Packages
              </span>
            </motion.div>

            <motion.h2
              className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Choose Your <span className="text-blue-600">Perfect Plan</span> for USCIS Forms
            </motion.h2>

            <motion.p
              className="text-gray-600 text-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Pick a plan that fits your workflow. Answer simple questions, pay securely, and download a
              ready-to-file PDF. When you need legal help, hand off your case to a licensed attorney or a law firm.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {plansData?.map((plan, index) => {
              const isFeatured =
                plan.name.includes("Professional") &&
                plan.billing_interval === "monthly";
              const isUnlimited = (val) => val === 0;
              const period =
                plan.billing_interval === "monthly" ? "/month" : "/year";

              return (
                <ScrollAnimation
                  key={plan.id}
                  direction="up"
                  delay={index * 0.1}
                >
                  <motion.div
                    className={`relative group ${isFeatured ? "md:-mt-1" : ""}`}
                    initial={{ opacity: 0, y: 50, rotateX: 45 }}
                    whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: index * 0.1,
                      duration: 0.8,
                      type: "spring",
                      stiffness: 100,
                    }}
                  >
                    <motion.div
                      className={`relative bg-white rounded-xl shadow-xl overflow-hidden h-full ${isFeatured
                        ? "border-2 border-blue-600"
                        : "border border-gray-200"
                        }`}
                      whileHover={{ y: -8, rotateY: 5 }}
                    >
                      {isFeatured && (
                        <motion.div
                          className="absolute -top-3 left-1/2 -translate-x-1/2 z-10"
                          initial={{ scale: 0, rotate: -180 }}
                          whileInView={{ scale: 1, rotate: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.5, type: "spring" }}
                        >
                          <div className="bg-blue-600 text-white px-3 py-0.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                            <svg
                              className="w-2.5 h-2.5"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            POPULAR
                          </div>
                        </motion.div>
                      )}
                      <div
                        className={`relative p-5 ${isFeatured
                          ? "bg-gradient-to-br from-blue-600 to-indigo-700"
                          : "bg-gradient-to-br from-gray-50 to-gray-100"
                          }`}
                      >
                        <div
                          className={`absolute -top-10 -right-10 w-28 h-28 rounded-full ${isFeatured ? "bg-white/10" : "bg-blue-100/50"
                            }`}
                        ></div>
                        <div className="relative">
                          <h3
                            className={`text-lg font-bold mb-2 ${isFeatured ? "text-white" : "text-gray-900"
                              }`}
                          >
                            {plan.name}
                          </h3>
                          <div className="flex items-baseline">
                            <motion.span
                              className={`text-3xl font-extrabold ${isFeatured ? "text-white" : "text-blue-600"
                                }`}
                              initial={{ scale: 0.5, opacity: 0 }}
                              whileInView={{ scale: 1, opacity: 1 }}
                              viewport={{ once: true }}
                              transition={{
                                delay: index * 0.1 + 0.3,
                                type: "spring",
                              }}
                            >
                              ${plan.price}
                            </motion.span>
                            <span
                              className={`ml-1.5 text-xs ${isFeatured ? "text-blue-100" : "text-gray-500"
                                }`}
                            >
                              {period}
                            </span>
                          </div>
                          <p
                            className={`mt-2 text-xs ${isFeatured ? "text-blue-100" : "text-gray-600"
                              }`}
                          >
                            Guided USCIS forms in plain English with smart checks and a ready-to-file PDF after secure checkout.
                          </p>
                        </div>
                      </div>

                      <div className="p-5">
                        <div className="grid grid-cols-2 gap-2.5 mb-4">
                          <motion.div
                            className="bg-blue-50 rounded-lg p-2 text-center"
                            whileHover={{ scale: 1.05 }}
                          >
                            <div className="text-lg font-bold text-blue-600">
                              {isUnlimited(plan.max_lawyers)
                                ? "Unlimited"
                                : plan.max_lawyers}
                            </div>
                            <div className="text-xs text-gray-600">Lawyers (max)</div>
                          </motion.div>
                          <motion.div
                            className="bg-blue-50 rounded-lg p-2 text-center"
                            whileHover={{ scale: 1.05 }}
                          >
                            <div className="text-lg font-bold text-blue-600">
                              {isUnlimited(plan.max_forms_per_month)
                                ? "Unlimited"
                                : plan.max_forms_per_month}
                            </div>
                            <div className="text-xs text-gray-600">Forms (per month)</div>
                          </motion.div>
                        </div>

                        <ul className="space-y-2 mb-5">
                          {plan.features.slice(0, 5).map((feature, idx) => (
                            <motion.li
                              key={idx}
                              className="flex items-start text-xs text-gray-600"
                              initial={{ opacity: 0, x: -20 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              viewport={{ once: true }}
                              transition={{ delay: index * 0.1 + idx * 0.1 }}
                            >
                              <motion.svg
                                className="w-3.5 h-3.5 text-blue-600 mr-2 shrink-0 mt-0.5"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                whileHover={{ scale: 1.3, rotate: 360 }}
                                transition={{ duration: 0.3 }}
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </motion.svg>
                              {feature}
                            </motion.li>
                          ))}
                          {plan.features.length > 5 && (
                            <li className="text-xs text-blue-600">
                              + {plan.features.length - 5} more
                            </li>
                          )}
                        </ul>

                        <div className="flex flex-col gap-2">
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <LanguageLink
                              to={`/register`}
                              className={`block w-full text-center py-2.5 rounded-lg font-semibold text-sm transition-all shadow-lg hover:shadow-xl ${isFeatured
                                ? "bg-blue-600 text-white hover:bg-blue-700"
                                : "bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                                }`}
                            >
                              Choose This Plan
                            </LanguageLink>
                          </motion.div>
                        </div>
                      </div>

                      <div
                        className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ${isFeatured ? "bg-blue-500/5" : "bg-blue-500/5"
                          }`}
                      ></div>
                    </motion.div>
                  </motion.div>
                </ScrollAnimation>
              );
            })}
          </div>
        </div>
      </section>

      {/* <PlanDetailsModal
        plan={planDetails}
        onClose={() => setSelectedPlanId(null)}
        isLoading={isFetchingDetails}
      /> */}
    </>
  );
};

export default PlansSection;
